import React, { ReactElement, ReactNode, useContext } from 'react';
import { PersonNameDTO } from '../../../../api/core/models';
import { Actions, MultiInputControls } from 'pages/components/form/MultiInput/MultiInputControls';
import { IntlShape, useIntl } from 'react-intl';
import { CheckPermissionFunction } from 'Auth/components/PermissionGate';
import { getDefaultPersonName } from '../../../../Settings/emptyModels';
import { PersonApi } from 'api/core/apis';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { getAuthMiddleware } from '../../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../../Auth/components/AuthProvider';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { InputSwitch } from 'primereact/inputswitch';
import { ToastContext, ToastContextType } from '../../../../util/providers/ToastProvider';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Panel } from '../layout/Panel';
import { FieldsetPersonName } from '../layout/Fieldset';

interface PersonNamesProps {
  title: string;
  checkPermission: CheckPermissionFunction;
  personNames: PersonNameDTO[];
  onChange: (personNames: PersonNameDTO[]) => void;
  validation: ValidationResult;
  autoFocus: boolean;
}

export function PersonNames(props: PersonNamesProps): ReactNode {
  const intl: IntlShape = useIntl();
  const {actions} = useContext(AuthContext);
  const toast: ToastContextType = useContext(ToastContext);
  const api: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));

  const deletePersonName = async (personName: PersonNameDTO, deleteAction: (e: React.MouseEvent<any>) => void, event: React.MouseEvent<any>): Promise<void> => {
    if (!personName.id && personName.id !== 0) {
      return deleteAction(event);
    }
    try {
      const workIds: number[] = await api.getWorksByPersonName({nameId: personName.id});
      if (workIds.length > 0) {
        return toast.alert({
          header: intl.formatMessage({id: 'message.deletion.nA.title'}),
          message: intl.formatMessage({id: 'message.deletion.nA.message'}, {amount: workIds.length}),
        });
      }
      return deleteAction(event);
    } catch (error) {
      toast.error(error, 'message.deletion.failed');
    }
  };


  return (
    <Panel
      title={props.title}
      toggleable={true}
    >
      <MultiInputControls<PersonNameDTO>
        lines={props.personNames}
        onChange={props.onChange}
        canEdit={props.checkPermission().canWrite}
        addNew={(rows: PersonNameDTO[]): number => rows.push({...getDefaultPersonName(), isMain: rows.length < 1})}
        renderLines={(personName: PersonNameDTO, onLineChange: (changedPersonNames: PersonNameDTO) => void, actions1: Actions, idx: number): ReactElement => (
          <div className={'col-12'}>
            <FieldsetPersonName
              pt={{legend: {className: 'py-2'}}}
              legend={
                <div className={'flex align-content-center'}>
                  <InputSwitch
                    id={`isMain${idx}`}
                    disabled={personName.isMain}
                    checked={personName.isMain}
                    onChange={(): void =>
                      props.onChange(
                        props.personNames.map((personNameDTO: PersonNameDTO, i: number): PersonNameDTO =>
                          ({...personNameDTO, isMain: i === idx})))}
                  ></InputSwitch>
                  <label className={'line-height-4 ml-3'}
                         htmlFor={`isMain${idx}`}>{intl.formatMessage({id: `${props.title}.attr.isMain.label`})}</label>
                </div>
              }
            >
              <InputGroup
                delete={{
                  action: (event: React.MouseEvent<any>): Promise<void> => deletePersonName(personName, actions1.delete, event),
                  disabled: !props.checkPermission().canWrite || personName.isMain,
                }}
              >
                <LabelledDropdown
                  id={`personName-${idx}-salutation`}
                  autoFocus={idx == 0 ? true : props.autoFocus}
                  className={'w-8rem'}
                  value={personName.salutation === '' ? 'none' : personName.salutation}
                  permissions={props.checkPermission('salutation')}
                  validation={props.validation?.children[idx]?.fields?.salutation}
                  options={getVocabularyOptions(intl.locale, 'salutation_list', '*')}
                  onChange={(salutation: any): void => onLineChange({...personName, salutation})}
                  label={`${props.title}.attr.salutation`}
                />
                <LabelledInputText
                  id={`personName-${idx}-givenName`}
                  value={personName.givenname}
                  permissions={props.checkPermission('givenname')}
                  validation={props.validation?.children[idx]?.fields?.givenname}
                  onChange={(givenname: string): void => onLineChange({...personName, givenname})}
                  label={`${props.title}.attr.givenName`}
                />
                <LabelledInputText
                  id={`personName-${idx}-surname`}
                  value={personName.surname}
                  permissions={props.checkPermission('surname')}
                  validation={props.validation?.children[idx]?.fields?.surname}
                  onChange={(surname: string): void => onLineChange({...personName, surname})}
                  label={`${props.title}.attr.surname`}
                />
              </InputGroup>
            </FieldsetPersonName>
          </div>
        )}
      />
    </Panel>
  );
}

